// TelcoTrack — hand off GeoJSON/DXF import mapping from Import hub → /maps side panel.

import { Injectable } from '@angular/core';
import type { ResolvedImportSourceCrs } from '@core/shared-bridge';

export interface GeometryImportHandoffLayer {
  layerName: string;
  featureCount: number;
  geometryTypes: string[];
  sampleProperties: Record<string, unknown>;
  mappedType?: string;
  visibleOnMap?: boolean;
}

export interface GeometryImportHandoffDraft {
  /** SPA path (+ query) to restore after mapping finishes / cancel. */
  returnUrl: string | null;
  lockPolygonId?: string;
  preferredPolygonId?: string;
  fileName?: string;
  parsedGeoJson: {
    type: 'FeatureCollection';
    features: Array<{
      type: 'Feature';
      geometry: { type: string; coordinates: unknown };
      properties: Record<string, unknown>;
    }>;
  } | null;
  /** Map sample for a large DXF whose full GeoJSON was not returned by analyze. */
  previewGeoJson?: GeometryImportHandoffDraft['parsedGeoJson'];
  pendingDxfText: string | null;
  layers: GeometryImportHandoffLayer[];
  totalFeatures: number;
  detectedSourceCrs: ResolvedImportSourceCrs | null;
  selectedSourceCrs: ResolvedImportSourceCrs | 'auto';
  drawingType: '1' | '2';
  selectedPolygonId: string;
  isNewPolygon: boolean;
  kiaCode: string;
  newPolygonName: string;
  newPolygonKiaCode: string;
  newPolygonRealizationYear: string;
  newPolygonFolderNumber: string;
  newPolygonConstructionType: string;
  newPolygonExpectedConstructionStart: string;
  newPolygonPlannedConstructionEnd: string;
  newPolygonDescription: string;
  newPolygonResponsibleBranch: string;
  newPolygonSapConstruction: string;
  newPolygonProjectState: string;
  newPolygonStavebnik: string;
  newPolygonProjektant: string;
  newPolygonZhotovitel: string;
  newPolygonStavbyveduciName: string;
  newPolygonStavebniPovoleniCislo: string;
  newPolygonObec: string;
}

@Injectable({ providedIn: 'root' })
export class GeometryImportHandoffService {
  private draft: GeometryImportHandoffDraft | null = null;
  /** Kept after takeDraft so /maps can still navigate back on cancel/success. */
  private pendingReturnUrl: string | null = null;

  stash(draft: GeometryImportHandoffDraft): void {
    this.draft = draft;
    const back = (draft.returnUrl ?? '').trim();
    this.pendingReturnUrl = back || null;
  }

  takeDraft(): GeometryImportHandoffDraft | null {
    const d = this.draft;
    this.draft = null;
    return d;
  }

  hasDraft(): boolean {
    return this.draft != null;
  }

  setReturnUrl(url: string | null): void {
    const back = (url ?? '').trim();
    this.pendingReturnUrl = back || null;
  }

  peekReturnUrl(): string | null {
    return this.pendingReturnUrl;
  }

  consumeReturnUrl(): string | null {
    const back = this.pendingReturnUrl;
    this.pendingReturnUrl = null;
    return back;
  }

  clear(): void {
    this.draft = null;
    this.pendingReturnUrl = null;
  }
}
